import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { Table } from "@/components/ui/Table";
import { Dialog } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { TextInput, TextareaInput, Checkbox } from "@/components/ui/Field";
import {
  createOrderEmailTemplate,
  deleteOrderEmailTemplate,
  listOrderEmailTemplates,
  updateOrderEmailTemplate,
  type OrderEmailTemplate,
} from "../../lib/settingsApi";

const PLACEHOLDERS = [
  { key: "{{kunde}}", desc: "Kundenname" },
  { key: "{{projekt}}", desc: "Projektbezeichnung" },
  { key: "{{bnr}}", desc: "Bestellnummer" },
];

export function OrderMailTemplatesSection() {
  const [items, setItems] = useState<OrderEmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState({ name: "", subject: "", body: "", is_default: false });
  const [formBaseline, setFormBaseline] = useState(form);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listOrderEmailTemplates());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Templates konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  function openEdit(t?: OrderEmailTemplate) {
    setEditId(t?.id ?? null);
    const initial = {
      name: t?.name || "",
      subject: t?.subject || "",
      body: t?.body || "",
      is_default: !!t?.is_default,
    };
    setForm(initial);
    setFormBaseline(initial);
    setDialogOpen(true);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) await updateOrderEmailTemplate(editId, form);
      else await createOrderEmailTemplate(form);
      setDialogOpen(false);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="Mail-Templates (Bestellungen)"
      description="Vorlagen für Bestell-Mails."
      actions={<Button type="button" onClick={() => openEdit()}>Neu</Button>}
    >
      {loading && <Spinner />}
      {error && <Alert variant="error" title="Fehler">{error}</Alert>}
      {!loading && (
        <Table<OrderEmailTemplate>
          rowKey={(r) => r.id}
          rows={items}
          stickyEndColumnCount={1}
          columns={[
            {
              key: "name",
              header: "Name",
              sortable: true,
              sortValue: (r) => r.name,
              render: (r) => r.name,
            },
            {
              key: "subject",
              header: "Betreff",
              sortable: true,
              sortValue: (r) => r.subject,
              render: (r) => r.subject,
            },
            {
              key: "default",
              header: "Standard",
              sortable: true,
              sortValue: (r) => r.is_default,
              render: (r) => (r.is_default ? "Ja" : "—"),
            },
            {
              key: "actions",
              header: "",
              render: (r) => (
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="secondary" onClick={() => openEdit(r)}>Bearbeiten</Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={async () => {
                      if (!window.confirm("Template löschen?")) return;
                      await deleteOrderEmailTemplate(r.id);
                      await reload();
                    }}
                  >
                    Löschen
                  </Button>
                </div>
              ),
            },
          ]}
        />
      )}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        dirty={hasFormChanges(form, formBaseline)}
        title={editId ? "Template bearbeiten" : "Neues Template"}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSubmit} loading={saving}>Speichern</Button>
          </>
        }
      >
        <form className="space-y-3" onSubmit={handleSubmit}>
          <TextInput label="Name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <TextInput label="Betreff" required value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} />
          <TextareaInput label="Text" required rows={10} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} />
          <Checkbox label="Als Standard verwenden" checked={form.is_default} onChange={(e) => setForm((f) => ({ ...f, is_default: e.target.checked }))} />
          <ul className="text-xs text-label-secondary">
            {PLACEHOLDERS.map((p) => (
              <li key={p.key}><code>{p.key}</code> — {p.desc}</li>
            ))}
          </ul>
        </form>
      </Dialog>
    </Card>
  );
}
